import { inject, Injectable } from '@angular/core';
import { GroupedTpaCoverage, PersonGroup } from '../models/tpa-group.model';
import { TpaSearchCoverageResExtended } from '../models/http/tpa-http.model';
import { TpaStatusFilter } from '../models/tpa.enum';
import { TpaGroupViewService } from '../components/tpa-details/components/tpa-group-view/services/tpa-group-view.service';
import { TpaGroupSortService } from './tpa-group-sort.service';
import { InsuranceTypeIconService } from '../../../shared/services/insurance-type-icon.service';

@Injectable({
  providedIn: 'root',
})
export class TpaGroupService {
  private tpaGroupViewService = inject(TpaGroupViewService);
  private tpaGroupSortService = inject(TpaGroupSortService);
  private insuranceTypeIconService = inject(InsuranceTypeIconService);

  groupCensusLines(censusLines: TpaSearchCoverageResExtended[]): GroupedTpaCoverage {
    const personsMap: Record<string, PersonGroup> = {};

    censusLines.forEach((line) => {
      if (!personsMap[line.personId]) {
        personsMap[line.personId] = {
          personName: line.insuredFullName,
          memberId: line.memberId,
          dateOfBirth: line.dateOfBirth,
          personId: line.personId,
          insuranceTypes: [],
        };
      }
      const person = personsMap[line.personId];

      let insuranceType = person.insuranceTypes.find(
        (it) => it.insuranceType === line.insuranceType,
      );
      if (!insuranceType) {
        insuranceType = {
          insuranceType: line.insuranceType,
          insuranceTypeName: line.insuranceTypeName,
          icon: this.insuranceTypeIconService.getIcon(line.insuranceTypeName),
          policies: [],
        };
        person.insuranceTypes.push(insuranceType);
      }

      let policy = insuranceType.policies.find((p) => p.policyNumber === line.policyNumber);
      if (!policy) {
        policy = {
          policyNumber: line.policyNumber,
          policyHolder: line.policyHolder,
          policyStartDate: line.policyStartDate,
          policyEndDate: line.policyEndDate,
          plans: [],
        };
        insuranceType.policies.push(policy);
      }

      let plan = policy.plans.find((p) => p.planNumber === line.planNumber);
      if (!plan) {
        plan = {
          planNumber: line.planNumber,
          planName: line.planName,
          coverArea: line.coverArea,
          hasActive: false,
          hasWaiting: false,
          items: [],
          planStartYear: '',
          planEndYear: '',
          tooltip: '',
        };
        policy.plans.push(plan);
      }

      plan.items.push(line);
    });

    // Post-process to calculate plan metadata
    Object.values(personsMap).forEach((person) => {
      person.insuranceTypes.forEach((insuranceType) => {
        insuranceType.policies.forEach((policy) => {
          policy.plans.forEach((plan) => {
            const yearRange = this.tpaGroupViewService.calculatePlanYearRange(plan.items);

            // Calculate plan metadata
            plan.items = plan.items.map((item) => ({
              ...item,
              cardConfig: this.tpaGroupViewService.setCardConfig(item),
            }));
            plan.hasActive = plan.items.some((item) => item.lineStatus === 'active');
            plan.hasWaiting = plan.items.some((item) => item.lineStatus === 'waiting');
            plan.tooltip = this.createPlanTooltipMessage(plan.items);
            plan.planStartYear = yearRange.startYear;
            plan.planEndYear = yearRange.endYear;
          });
        });
      });
    });

    return this.tpaGroupSortService.sortGroupedData({
      persons: Object.values(personsMap),
    });
  }

  filterCensusLines(censusLines: TpaSearchCoverageResExtended[], filter: TpaStatusFilter) {
    if (filter === TpaStatusFilter.All) {
      return censusLines;
    }

    if (filter === TpaStatusFilter.Draft) {
      // Draft filter shows "Waiting" lines (insurance that will come)
      return censusLines.filter((line) => line.lineStatus === 'waiting');
    }

    if (filter === TpaStatusFilter.Active) {
      // Draft policy status is considered valid/active, so include draft lines based on their date status
      return censusLines.filter((line) => line.lineStatus === 'active');
    }

    if (filter === TpaStatusFilter.Inactive) {
      // Show only terminated lines (waiting has its own filter)
      return censusLines.filter((line) => line.lineStatus === 'terminated');
    }

    return censusLines;
  }

  private createPlanTooltipMessage(items: TpaSearchCoverageResExtended[]): string {
    const hasActive = items.some((item) => item.lineStatus === 'active');
    const hasWaiting = items.some((item) => item.lineStatus === 'waiting');
    const hasTerminated = items.some((item) => item.lineStatus === 'terminated');

    const statuses: string[] = [];
    if (hasActive) statuses.push('active');
    if (hasWaiting) statuses.push('waiting');
    if (hasTerminated) statuses.push('terminated');

    return `This plan contains ${statuses.join(', ')} census lines.`;
  }
}
